/**
 * MemberDataSourceCard
 * Lets System Admins choose where member/owner data is read from:
 *   jonas      → Jonas DataMart (DB2). default for Jonas-integrated HOAs
 *   standalone → Local Property table . for HOAs that don't use Jonas
 *   hybrid     → Both: Property for addresses, Jonas for member profiles
 */
import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, RadioGroup, FormControlLabel, Radio,
  Button, Alert, CircularProgress, Chip, Divider,
} from "@mui/material";
import StorageIcon       from "@mui/icons-material/Storage";
import CloudOffIcon      from "@mui/icons-material/CloudOff";
import MergeIcon         from "@mui/icons-material/MergeType";
import HomeWorkIcon      from "@mui/icons-material/HomeWork";
import api from "../../utils/api";

const MODES = [
  {
    value: "jonas",
    label: "Jonas Integration",
    icon: <StorageIcon sx={{ fontSize: 20, color: "#2f63ad" }} />,
    desc: "Member names, emails and contact info come from the Jonas DataMart (DB2). Required when HOA membership is managed in Jonas.",
    badge: "Default",
    badgeColor: "#2f63ad",
  },
  {
    value: "standalone",
    label: "Standalone (Properties)",
    icon: <HomeWorkIcon sx={{ fontSize: 20, color: "#16a34a" }} />,
    desc: "No Jonas connection needed. All owner/member info is stored in the local Property table. Ideal for HOAs that don't use Jonas.",
    badge: "No DB2 required",
    badgeColor: "#16a34a",
  },
  {
    value: "hybrid",
    label: "Hybrid",
    icon: <MergeIcon sx={{ fontSize: 20, color: "#d97706" }} />,
    desc: "Properties (common areas, physical units) live locally. Owner profiles come from Jonas via Property.external_id → ClubMemberCode. Use when some properties have no Jonas member.",
    badge: "Advanced",
    badgeColor: "#d97706",
  },
];

export default function MemberDataSourceCard() {
  const [mode,    setMode]    = useState("jonas");
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    api.get("/app-settings")
      .then(r => { setMode(r.data?.member_data_source || "jonas"); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      await api.put("/app-settings", { member_data_source: mode });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save setting.");
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ py: 2, textAlign: "center" }}><CircularProgress size={20} /></Box>;

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(47,99,173,0.10)", mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <StorageIcon sx={{ color: "#2f63ad", fontSize: 22 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
          Member Data Source
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
        Controls where member names, emails and contact info are read from across
        work orders, violations, architectural requests, email notifications, and the AI assistant.
        Takes effect immediately. no restart required.
      </Typography>

      <Divider sx={{ mb: 2.5 }} />

      <RadioGroup value={mode} onChange={e => setMode(e.target.value)}>
        {MODES.map(m => (
          <Box key={m.value} onClick={() => setMode(m.value)} sx={{
            display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, mb: 1.5,
            borderRadius: "12px", cursor: "pointer",
            border: `1.5px solid ${mode === m.value ? m.badgeColor : "rgba(47,99,173,0.08)"}`,
            backgroundColor: mode === m.value ? `${m.badgeColor}08` : "#fafbff",
            transition: "all 0.15s ease",
          }}>
            <FormControlLabel value={m.value} control={<Radio sx={{ color: m.badgeColor, p: 0.5 }} />} label="" sx={{ m: 0, p: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                {m.icon}
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>{m.label}</Typography>
                <Chip label={m.badge} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, backgroundColor: `${m.badgeColor}18`, color: m.badgeColor }} />
              </Box>
              <Typography variant="caption" sx={{ color: "#6b778c", lineHeight: 1.5, display: "block" }}>{m.desc}</Typography>
            </Box>
          </Box>
        ))}
      </RadioGroup>

      {mode === "standalone" && (
        <Alert severity="info" sx={{ borderRadius: "10px", mb: 2, fontSize: "0.78rem" }}>
          In standalone mode, go to <strong>Properties &amp; Members → Properties</strong> to create and manage property/owner records.
          The <em>mailing_name</em>, <em>emails</em> and <em>phone_numbers</em> fields on each Property become the member's contact info.
        </Alert>
      )}
      {mode === "hybrid" && (
        <Alert severity="warning" sx={{ borderRadius: "10px", mb: 2, fontSize: "0.78rem" }}>
          Hybrid mode requires <strong>Property.external_id</strong> to match the Jonas <strong>ClubMemberCode</strong> for properties that have a Jonas member.
          Properties without an external_id will use their own contact fields.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ borderRadius: "10px", mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ borderRadius: "10px", mb: 2 }}>Setting saved. effective immediately.</Alert>}

      <Button variant="contained" onClick={handleSave} disabled={saving}
        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, background: "#2f63ad", "&:hover": { background: "#1e4c8a" } }}>
        {saving ? <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} /> : null}
        {saving ? "Saving…" : "Save Data Source"}
      </Button>
    </Paper>
  );
}
